// updated 2026-05-10
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Hint from './Hint';
import { computePriceAverages } from '../utils/itemPrices';
import { fetchMarketPrices, matchMarketItem, compareUserVsMarket } from '../utils/marketPrices';
import { fetchManyNaverPrices, compareUserVsNaver } from '../utils/naverPrices';
import { canonicalize as aiCanonicalize } from '../utils/aiMapper';
import { sameIngredientName } from '../utils/ingredientMatch';
import { exportSingleSheet, buildPricesSheet } from '../utils/backupExport';
import { classifyItem, CATEGORY_KEY_TO_LABEL } from '../utils/itemCategory';
import { saveManualTranslation, fillPriceZh, priceRowZhNeeds, PRICE_ZH_FIELDS, validCompanyZh } from '../utils/translate';
import BiName from '../i18n/BiName';
import { specToZh, unitToZh } from '../i18n/units';
import { makeCodeAllocator, assignMissingCodes } from '../utils/priceCodes';
import { searchVariants } from '../utils/hangulKeys';   // V122: 영타로 친 한글도 검색
import { pronText, isPinyinReady, ensurePinyin } from '../i18n/pron';   // V127 엑셀 발음
import { normalizeLinkInput, safeLinkHref, linkLabel } from '../utils/purchaseLink';   // V118 구매 링크
import { useLang } from '../i18n/LangContext';   // V105 UI 다국어
import VendorPicker from './VendorPicker';   // V132 발주처 고르기
import InquiryListModal from './InquiryListModal';   // V143 다른 업체에 문의 (위챗 목록)
import PrintPicker from './PrintPicker';   // V154 단가표(+레시피) 골라서 인쇄
import { usesLoss, lossG, usableG, pricePerUsableKg, yieldPct, FRESH_CATEGORY, NET_CATEGORY } from '../utils/priceYield';   // V152
import { josa } from '../utils/semiClash';   // V153 조사
import { ACCTS, ACCT_LABEL, acctOfRow } from '../utils/receiving';   // V159 계정과목
import { koNameOrSound } from '../utils/koNameFill';   // V161 중국어로 적으면 한국어 자동 · V162 안 되면 병음 한글
import { splitKoZh, hasHangul, hasCjk, isZhOnly } from '../utils/koZh';

// ── V161: 재료명 칸 정리 ─────────────────────────────────────────────────────
// 사장님: "중국인 직원한테 입력을 맡길 거라 중국어로 품목을 입력할 거야. 그럼 한국어 입력이 자동으로 돼야 해"
//         "중국어 입력된 것에 한국어 입력() 하고 중국어도 중복으로 쓰는데 그렇지 않게"
//   · '청경채(上海青)'  → 재료명 '청경채' · 중국어 '上海青'
//   · '上海青' (중국어만) 또는 재료명은 비우고 중국어만 → 중국어 '上海青' · 재료명은 한국어로 번역해서
// @returns { row, needKo, zhFromName }
export function tidyNameFields(row) {
  const sv = v => String(v == null ? '' : v).trim();
  let name = sv(row?.name);
  let zh = sv(row?.name_zh);
  let needKo = false; let zhFromName = false;
  if (hasHangul(name) && hasCjk(name)) {
    const sp = splitKoZh(name);
    if (sp.ko && sp.zh) {
      // 중국어 칸이 비었으면 이름 속 중국어로. 직접 적어 둔 중국어는 그대로 (V164)
      if (!zh || hasHangul(zh)) { zh = sp.zh; zhFromName = true; }
      name = sp.ko;
    }
  } else if (isZhOnly(name)) {
    if (!zh || hasHangul(zh)) zh = name;
    zhFromName = true;
    needKo = true;
  } else if (!name && zh && !hasHangul(zh)) {
    needKo = true;
  }
  return { row: { ...row, name: name || zh, name_zh: zh }, needKo, zhFromName };
}
const withTimeout = (pr, ms) => Promise.race([pr, new Promise(r => setTimeout(() => r(null), ms))]);
async function koNameFor(zh) {
  try {
    const ko = await withTimeout(koNameOrSound(zh, 5000), 8000);
    return ko && hasHangul(ko) ? String(ko).trim() : '';
  } catch { return ''; }
}

// V80: 신선식품 / 공산품 / 소모품 + 반제품. 기존 '원재료'는 자동 추론으로 마이그.
const PRICE_CATS = ['신선식품', '공산품', '소모품', '반제품'];

// 기존 '원재료' 항목을 키워드 기반으로 신선/공산/소모로 자동 추론.
// 명시 카테고리가 있고 새 4종 중 하나면 그대로 유지.
function resolveDisplayCategory(p, semiNames) {
  if (semiNames.has(p.name)) return '반제품';
  const raw = p.category;
  if (raw && (raw === '신선식품' || raw === '공산품' || raw === '소모품' || raw === '반제품')) return raw;
  // legacy: '원재료' or empty → classifyItem 추론
  const key = classifyItem(p);
  // semi/other는 단가표 입장에선 신선식품 기본값으로 노출 (사용자가 직접 재분류)
  if (key === 'semi') return '반제품';
  return CATEGORY_KEY_TO_LABEL[key] === '미분류' ? '신선식품' : CATEGORY_KEY_TO_LABEL[key];
}

function PriceManager({ prices, onPricesChange, categories = [], onCategoriesChange, user }) {
  // V112: 중국 매장은 한국 시장가(KAMIS 도매가·네이버 최저가)가 의미 없다.
  // 원화 시세를 ¥로 찍으면 오히려 틀린 정보가 되므로 조회도, 열도 숨긴다.
  const { country: storeCountry } = useLang();
  const isCN = storeCountry === 'CN';
  // ── 가격 인텔리전스 — 지난 30일 영수증 OCR 평균가 ─────────────────────────
  const [priceAverages, setPriceAverages] = useState(new Map());
  useEffect(() => {
    if (!user?.uid || !prices || prices.length === 0) return;
    let cancelled = false;
    computePriceAverages(user.uid, prices, 30).then(map => {
      if (!cancelled) setPriceAverages(map);
    }).catch(e => console.warn('priceAverages failed:', e));
    return () => { cancelled = true; };
  }, [user?.uid, prices?.length]);  // prices 길이 바뀔 때만 재계산

  // ── KAMIS 시장 도매가 ──────────────────────────────────────────────────
  const [marketItems, setMarketItems] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketUpdatedAt, setMarketUpdatedAt] = useState(null);
  useEffect(() => {
    if (storeCountry === 'CN') return;
    let cancelled = false;
    setMarketLoading(true);
    fetchMarketPrices().then(data => {
      if (cancelled) return;
      if (data.ok) {
        setMarketItems(data.items || []);
        setMarketUpdatedAt(data.updatedAt);
      }
      setMarketLoading(false);
    }).catch(e => {
      console.warn('marketPrices failed:', e);
      if (!cancelled) setMarketLoading(false);
    });
    return () => { cancelled = true; };
  }, [storeCountry]);  // 매장 국가가 정해지면 한 번 (중국 매장은 건너뜀)

  // ── 네이버 쇼핑 최저가 (버튼 트리거 — API 호출 비용 관리) ──────────────
  const [naverPrices, setNaverPrices] = useState(new Map()); // name → naver data
  const [naverLoading, setNaverLoading] = useState(false);
  const [naverFetched, setNaverFetched] = useState(false);
  const loadNaverPrices = async () => {
    if (!prices || prices.length === 0) return;
    setNaverLoading(true);
    try {
      const map = await fetchManyNaverPrices(prices, 3);
      setNaverPrices(map);
      setNaverFetched(true);
    } catch (e) {
      console.warn('naverPrices failed:', e);
    } finally {
      setNaverLoading(false);
    }
  };

  // ── AI 매칭 향상 (Claude Haiku canonicalize) ──────────────
  // originalName → AI 표준명. 매칭 안 된 항목 재시도용.
  const [aiCanonicalMap, setAiCanonicalMap] = useState(new Map());
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState({ done: 0, total: 0 });

  // ── 원클릭 통합 조회 상태 ──
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoStep, setAutoStep] = useState('idle'); // idle | kamis | naver | ai | done
  const [autoMessage, setAutoMessage] = useState('');
  const [autoSummary, setAutoSummary] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 핵심 로직: silent=true면 alert 안 띄움 (autoFetch에서 사용)
  const enhanceWithAi = async (silent = false) => {
    if (!prices || prices.length === 0) return { enhanced: 0, total: 0 };
    // KAMIS·Naver 둘 다 매칭 안 된 항목 우선
    const targets = (prices || [])
      .filter(p => !semiNames.has(p.name) && !menuNames.has(p.name))
      .filter(p => {
        const km = matchMarketItem(p, marketItems);
        const nv = naverPrices.get(p.name);
        const naverOk = nv && nv.items && nv.items.length > 0;
        const kmOk = km && km.pricePerKg > 0;
        return !kmOk && !naverOk;
      })
      .filter(p => !aiCanonicalMap.has(p.name));

    if (targets.length === 0) {
      if (!silent) alert('AI 매칭이 필요한 미매칭 항목이 없습니다.');
      return { enhanced: 0, total: 0 };
    }

    setAiLoading(true);
    setAiProgress({ done: 0, total: targets.length });
    const newMap = new Map(aiCanonicalMap);
    let processed = 0;
    for (const p of targets) {
      try {
        const canonical = await aiCanonicalize(p.name);
        if (canonical && canonical !== p.name) {
          newMap.set(p.name, canonical);
        }
      } catch (e) {
        console.warn('AI canonicalize failed for', p.name, e);
      }
      processed++;
      setAiProgress({ done: processed, total: targets.length });
      if (processed % 5 === 0) await new Promise(r => setTimeout(r, 0));
    }
    setAiCanonicalMap(newMap);
    setAiLoading(false);

    const enhanced = Array.from(newMap.entries())
      .filter(([orig]) => targets.find(t => t.name === orig))
      .length;
    if (!silent) alert(`AI 매칭 완료: ${enhanced}/${targets.length}개 항목 표준명 추정`);
    return { enhanced, total: targets.length };
  };

  // ── 원클릭 통합 조회: 시장가 → 네이버 → AI 미매칭 추정 ──
  const runAutoFetch = async () => {
    if (autoRunning) return;
    if (!prices || prices.length === 0) {
      alert('단가표에 항목을 먼저 등록하세요.');
      return;
    }
    setAutoRunning(true);
    setAutoSummary(null);

    // 1단계: 시장가 (이미 자동 로드되지만 사용자가 명시적으로 다시 확인하도록)
    setAutoStep('kamis');
    setAutoMessage('1/3 시장가(KAMIS) 조회 중...');
    try {
      // marketItems가 비어있으면 fetch
      if (!marketItems || marketItems.length === 0) {
        setMarketLoading(true);
        const data = await fetchMarketPrices();
        if (data?.ok) {
          setMarketItems(data.items || []);
          setMarketUpdatedAt(data.updatedAt);
        }
        setMarketLoading(false);
      }
    } catch (e) {
      console.warn('KAMIS fetch failed:', e);
    }
    await new Promise(r => setTimeout(r, 100));

    // 2단계: 네이버
    setAutoStep('naver');
    setAutoMessage('2/3 네이버 가격 조회 중...');
    setNaverLoading(true);
    try {
      const map = await fetchManyNaverPrices(prices, 3);
      setNaverPrices(map);
      setNaverFetched(true);
    } catch (e) {
      console.warn('naver fetch failed:', e);
    } finally {
      setNaverLoading(false);
    }
    await new Promise(r => setTimeout(r, 100));

    // 3단계: AI 미매칭 추정
    setAutoStep('ai');
    setAutoMessage('3/3 AI 매칭 향상 중...');
    const aiResult = await enhanceWithAi(true);

    // 결과 집계
    setAutoStep('done');
    const totalItems = (prices || []).filter(p => !semiNames.has(p.name) && !menuNames.has(p.name)).length;
    let kamisOk = 0, naverOk = 0, aiOk = 0;
    for (const p of prices || []) {
      if (semiNames.has(p.name) || menuNames.has(p.name)) continue;
      const km = matchMarketItem(p, marketItems);
      if (km && km.pricePerKg > 0) kamisOk++;
      const nv = naverPrices.get(p.name);
      if (nv && nv.items?.length > 0) naverOk++;
    }
    aiOk = aiResult.enhanced;

    setAutoSummary({
      total: totalItems,
      kamis: kamisOk,
      naver: naverOk,
      ai: aiOk,
      unmatched: Math.max(0, totalItems - kamisOk - naverOk - aiOk),
    });
    setAutoMessage('');
    setAutoRunning(false);

    // 5초 후 자동으로 done 상태 해제 (요약은 유지)
    setTimeout(() => setAutoStep('idle'), 100);
  };

  // 캐싱된 canonical 이름으로 KAMIS 재매칭 시도
  const matchWithAiFallback = (row) => {
    const km = matchMarketItem(row, marketItems);
    if (km && km.pricePerKg > 0) return km;
    const canonical = aiCanonicalMap.get(row.name);
    if (canonical && canonical !== row.name) {
      return matchMarketItem({ ...row, name: canonical }, marketItems);
    }
    return km;
  };

  // 캐싱된 canonical 이름으로 Naver 재매칭 시도
  const naverWithAiFallback = (row) => {
    const direct = naverPrices.get(row.name);
    if (direct && direct.items?.length > 0) return direct;
    const canonical = aiCanonicalMap.get(row.name);
    if (canonical) return naverPrices.get(canonical);
    return direct;
  };

  // 반제품 이름 목록 (type='semi')
  const semiNames = useMemo(() => new Set(
    categories.flatMap(c => c.items.filter(i => i.type === 'semi').map(i => i.name))
  ), [categories]);

  // 판매 메뉴 이름 목록 (type='menu') → 단가표에서 제외
  const menuNames = useMemo(() => new Set(
    categories.flatMap(c => c.items.filter(i => i.type === 'menu').map(i => i.name))
  ), [categories]);

  const [tab, setTab] = useState('전체');
  const [showInquiry, setShowInquiry] = useState(false);   // V143 다른 업체에 문의
  const [showPrint, setShowPrint] = useState(false);       // V154 인쇄 고르기
  const [search, setSearch] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState({ code: '', name: '', spec: '', unit: 'g', price: '', net_g: '', category: '신선식품', vendor: '', maker: '' });

  // V80: 카테고리 자동 분류 + 판매 메뉴 제외
  //   1) 판매 메뉴(type='menu')는 단가표에서 제외
  //   2) 반제품(type='semi') → '반제품'
  //   3) 기존 명시 카테고리 4종(신선식품/공산품/소모품/반제품) → 그대로 유지
  //   4) 그 외 ('원재료' legacy 포함) → classifyItem 키워드 추론으로 신선/공산/소모
  const normalizedPrices = useMemo(() =>
    prices
      .filter(p => !menuNames.has(p.name))
      .map(p => ({
        ...p,
        category: resolveDisplayCategory(p, semiNames),
      })),
    [prices, semiNames, menuNames]
  );

  // V112: 번역이 끝나는 몇 초 사이 다른 품목이 저장될 수 있다. 패치는 항상 '지금' 목록에 건다.
  // (예전엔 저장 시점 목록으로 덮어써서, 그 사이 추가한 품목이 사라질 수 있었다)
  const latestPricesRef = useRef(normalizedPrices);
  latestPricesRef.current = normalizedPrices;
  // V121: 저장하자마자 '지금 목록'도 바꿔 둔다. 화면이 다시 그려지기 전에 번역 패치가 끝나면
  //       옛 목록(고치기 전 단가)에 패치를 걸어 방금 고친 값을 되돌려 버릴 수 있었다.
  // V169: 판매 메뉴와 이름이 같은 단가표 줄은 화면(normalizedPrices)에서 빠지는데, 예전엔 저장할 때 그 화면 목록으로 덮어써서
  //       그런 줄이 단가표에서 통째로 지워졌다 (예: 메뉴 '카스 500ml'과 단가표 '카스 500ml'). 저장할 때 다시 붙인다.
  const hiddenRowsRef = useRef([]);
  hiddenRowsRef.current = (prices || []).filter(p => p && menuNames.has(p.name));
  const commitPrices = (next) => {
    latestPricesRef.current = next;
    const hidden = hiddenRowsRef.current;
    if (!hidden.length) { onPricesChange(next); return; }
    const has = new Set(next.map(r => `${r.code || ''}|${r.name}`));
    onPricesChange([...next, ...hidden.filter(h => !has.has(`${h.code || ''}|${h.name}`))]);
  };

  // ── V113: 빈 코드 자동 번호 ─────────────────────────────────────────────
  // 코드는 영수증 매입가를 품목에 묶는 열쇠라, 비어 있으면 "내 평균가(30일)"가 안 쌓인다.
  // 단가표를 열 때 코드가 빈 품목에만 P+순번을 매긴다 (열 때마다 한 번 확인, 빈 게 없으면 아무것도 안 함).
  // 화면용 normalizedPrices가 아니라 원본 prices에 매겨서, 코드 말고는 저장 데이터가 하나도 안 바뀌게 한다.
  const codeFillDone = useRef(false);
  useEffect(() => {
    if (codeFillDone.current || !Array.isArray(prices) || prices.length === 0) return;
    codeFillDone.current = true;
    const { next, assigned } = assignMissingCodes(prices);
    if (assigned > 0) onPricesChange(next);
  }, [prices]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => ({
    전체:    normalizedPrices.length,
    신선식품: normalizedPrices.filter(p => p.category === '신선식품').length,
    공산품:   normalizedPrices.filter(p => p.category === '공산품').length,
    소모품:   normalizedPrices.filter(p => p.category === '소모품').length,
    반제품:   normalizedPrices.filter(p => p.category === '반제품').length,
  }), [normalizedPrices]);

  // V81: 1차 vendor, 2차 category 순서 (신선→공산→소모→반제품), 3차 name
  const CATEGORY_SORT_ORDER = { '신선식품': 1, '공산품': 2, '소모품': 3, '반제품': 4 };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qs = q ? [...new Set(searchVariants(search.trim()).map(v => v.toLowerCase()))] : [];   // V122
    const hit = (p, v) => p.name?.toLowerCase().includes(v) || p.code?.toLowerCase().includes(v)
      || p.name_zh?.includes(v) || p.maker?.toLowerCase().includes(v) || p.vendor?.toLowerCase().includes(v);
    const arr = normalizedPrices
      .map((p, i) => ({ ...p, _idx: i }))
      .filter(p => tab === '전체' || p.category === tab)
      .filter(p => !q || qs.some(v => hit(p, v)));

    // 정렬: 업체 → 카테고리 → 이름
    arr.sort((a, b) => {
      const va = (a.vendor || '').trim();
      const vb = (b.vendor || '').trim();
      // 빈 vendor는 맨 뒤
      if (va && !vb) return -1;
      if (!va && vb) return 1;
      if (va !== vb) return va.localeCompare(vb, 'ko');
      const ca = CATEGORY_SORT_ORDER[a.category] || 9;
      const cb = CATEGORY_SORT_ORDER[b.category] || 9;
      if (ca !== cb) return ca - cb;
      return (a.name || '').localeCompare(b.name || '', 'ko');
    });
    return arr;
  }, [normalizedPrices, tab, search]);

  const [focusCol, setFocusCol] = useState(null);
  // V132: 지금까지 입력한 발주처 목록 (많이 쓴 순) — 발주처 칸을 누르면 골라 쓸 수 있게
  const vendorOptions = useMemo(() => {
    const m = new Map();
    for (const p of prices || []) {
      const v = String(p?.vendor || '').trim();
      if (!v) continue;
      const cur = m.get(v) || { name: v, zh: '', count: 0 };
      cur.count++;
      const z = String(p.vendor_zh || '').trim();
      if (!cur.zh && z && !/[가-힣]/.test(z)) cur.zh = z;
      m.set(v, cur);
    }
    return [...m.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'ko'));
  }, [prices]);
  const { t, fmt: money, unit, lang } = useLang();       // V105 문구 / V108 매장 통화
  // ── V152: 중량 — 신선식품은 구매 중량 + 로스(실사용량으로 원가), 공산품은 NET 중량, 그 밖은 기준량 ──
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  const weightPlaceholder = (cat) => (cat === FRESH_CATEGORY ? Z('구매 중량', '购买重量')
    : cat === NET_CATEGORY ? Z('NET 중량', '净含量') : t('기준량'));
  // 저장 전 확인 — 로스는 구매 중량보다 작아야 한다. 문제가 없으면 '' 
  const lossProblem = (row) => {
    if (!usesLoss(row)) return '';
    const loss = Number(row.loss_g) || 0;
    if (loss < 0) return Z('로스 중량은 0보다 작을 수 없어요.', '损耗重量不能小于 0。');
    if (loss > 0 && loss >= (Number(row.net_g) || 0)) return Z('로스 중량은 구매 중량보다 작아야 해요.', '损耗重量必须小于购买重量。');
    return '';
  };
  // 저장할 줄에 로스 칸 정리 — 신선식품이고 0보다 클 때만 남긴다
  const withLoss = (row, src) => {
    const out = { ...row };
    const loss = Number(src.loss_g) || 0;
    if (usesLoss(out) && loss > 0) out.loss_g = loss; else delete out.loss_g;
    return out;
  };
  // V153: 식재료 이름이 반제품 레시피 이름과 겹치지 않게 (같은 이름이면 반제품과 식재료가 하나로 섞인다)
  const semiRecipeNames = (categories || []).flatMap(c => (c && c.items) || []).filter(r => r && r.type === 'semi').map(r => r.name);
  const clashMsg = (row, prevRow) => {
    const n = String(row.name || '').trim();
    if (!n) return '';
    const changed = !prevRow || !sameIngredientName(n, prevRow.name) || row.category !== prevRow.category;
    if (!changed) return '';
    if (row.category !== '반제품' && semiRecipeNames.some(sn => sameIngredientName(sn, n))) {
      return Z(`${josa(`'${n}'`, '은/는')} 반제품 레시피 이름이에요.\n식재료는 다른 이름으로 지어 주세요 (예: ${n}(원재료)).\n이미 겹쳐 있다면 레시피 화면의 '⚠ 이름 바꾸고 고치기'로 고칠 수 있어요.`,
               `「${n}」是半成品配方的名字。\n食材请换个名字（例：${n}(原料)）。\n如果已经重名，可以在配方页面用「⚠ 改名并修好」来改。`);
    }
    if (row.category === '반제품' && normalizedPrices.some(p => p !== prevRow && p.category !== '반제품' && sameIngredientName(p.name, n))) {
      return Z(`단가표에 같은 이름의 식재료 ${josa(`'${n}'`, '이/가')} 있어요. 반제품 줄은 다른 이름으로 해 주세요.`,
               `单价表里已经有同名的食材「${n}」。半成品这一行请换个名字。`);
    }
    return '';
  };
  // 입력 중 미리보기 (= 실사용 4500g)
  const lossPreview = (row) => {
    if (!usesLoss(row)) return null;
    const net = Number(row.net_g) || 0;
    const loss = Number(row.loss_g) || 0;
    if (!net || !loss) return null;
    if (loss >= net) return <div data-loss-warn style={{ fontSize: 10, color: '#DC2626', marginTop: 3 }}>{Z('로스가 구매 중량보다 커요', '损耗大于购买重量')}</div>;
    return <div data-loss-preview style={{ fontSize: 10, color: '#16A34A', marginTop: 3, whiteSpace: 'nowrap', textAlign: 'right' }}>= {Z('실사용', '实际可用')} {net - loss}g</div>;
  };
  const [zhRunning, setZhRunning]   = useState(false);
  const [zhProgress, setZhProgress] = useState({ done: 0, total: 0 });

  // V121: 편집 중에 다른 화면의 변경이 실시간으로 들어오면 줄 순서가 바뀔 수 있다.
  //       번호만 믿고 저장하면 엉뚱한 품목을 덮어쓰므로, 편집을 시작한 품목(코드|이름)을 기억해 둔다.
  const editKeyRef = useRef(null);
  const rowKey = (r) => `${String((r && r.code) || '').trim()}|${String((r && r.name) || '').trim()}`;

  const startEdit = (idx, col = null) => {
    setEditingIdx(idx);
    editKeyRef.current = rowKey(normalizedPrices[idx]);
    setEditRow({ ...normalizedPrices[idx] });
    setFocusCol(col);
    setIsAdding(false);
  };

  const cancelEdit = () => { setEditingIdx(null); setEditRow(null); };

  const handleEditKeyDown = (e) => {
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;   // V161 중국어 입력기 조합 중 Enter
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // 행 바깥으로 포커스가 이동하면 자동 저장
  const handleEditBlur = (e) => {
    const related = e.relatedTarget;
    // 같은 편집 행 안에 있는 요소면 저장하지 않음
    if (related && related.closest && related.closest('[data-edit-row]')) return;
    // 짧은 딜레이 후 저장 (버튼 클릭 이벤트가 먼저 실행되도록)
    setTimeout(() => {
      if (editingIdx !== null) saveEdit();
    }, 100);
  };

  const handleNewKeyDown = (e) => {
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;   // V161
    if (e.key === 'Enter') saveNew();
    if (e.key === 'Escape') setIsAdding(false);
  };

  // ── V112 중국어 병기 (재료명·제조사·발주처·규격·단위) ──────────────────
  // 저장은 즉시 끝내고 번역은 뒤에서 채운다. 네트워크가 죽어도 저장은 성공.
  // 사람이 직접 적은 중국어는 공용 캐시에 manual로 잠가 AI가 다시 덮지 않게 한다.
  // 번역 결과를 '지금' 목록에 붙인다. pairs = [[번역 보낸 행, 번역된 행], ...]
  // 방금 저장한 내용이 아직 화면 목록에 반영되기 전일 수 있다(렌더 대기).
  // 이름만 같은 '수정 전' 행에 패치하면 안 되므로 한국어 칸이 보낸 값과 전부 같은 행에만 붙이고,
  // 그런 행이 아직 없으면 잠깐씩 기다린다. (그 사이 또 고쳐서 영영 안 같아지면 10번 뒤 포기 — 그 번역은 이미 낡은 것)
  // V118: 업체명 중국어에 한글이 섞여 있으면(옛 번역 "청도식品") 빈 칸처럼 보고 새 번역으로 바꾼다.
  const zsv = v => String(v == null ? '' : v).trim();
  const koSig = (p) => PRICE_ZH_FIELDS.map(([f]) => zsv(p && p[f])).join('\u0001');
  const applyZhPatches = (pairs, attempt = 0) => {
    const bySig = new Map(pairs.filter(([r, tr]) => r && tr).map(([r, tr]) => [koSig(r), tr]));
    if (!bySig.size) return;
    const cur = latestPricesRef.current || [];
    if (!cur.some(p => bySig.has(koSig(p)))) {
      if (attempt < 10) setTimeout(() => applyZhPatches(pairs, attempt + 1), 150);
      return;
    }
    let changed = false;
    const latest = cur.map(p => {
      const tr = bySig.get(koSig(p));
      if (!tr) return p;
      const out = { ...p };
      for (const [f, zf] of PRICE_ZH_FIELDS) {
        const isCompany = f === 'maker' || f === 'vendor';
        const empty = !zsv(p[zf]) || (isCompany && !validCompanyZh(p[zf]));
        const val = zsv(tr[zf]);
        if (val && empty && (!isCompany || validCompanyZh(val))) { out[zf] = val; changed = true; }
      }
      return out;
    });
    if (changed) commitPrices(latest);
  };

  const afterSaveZh = (row, prev = {}) => {
    const sv = v => String(v == null ? '' : v).trim();
    if (sv(row.name) && sv(row.name_zh) && sv(row.name_zh) !== sv(prev.name_zh)) saveManualTranslation(sv(row.name), sv(row.name_zh), 'item');
    if (sv(row.maker) && sv(row.maker_zh) && sv(row.maker_zh) !== sv(prev.maker_zh)) saveManualTranslation(sv(row.maker), sv(row.maker_zh), 'vendor');
    if (sv(row.vendor) && sv(row.vendor_zh) && sv(row.vendor_zh) !== sv(prev.vendor_zh)) saveManualTranslation(sv(row.vendor), sv(row.vendor_zh), 'vendor');
    fillPriceZh([row]).then(({ next, filled }) => {
      if (filled) applyZhPatches([[row, next[0]]]);
    }).catch(() => {});
  };

  // ── V118: 중국어 빈 칸 자동 보충 ──────────────────────────────────────────
  // 저장 직후 뒤에서 하는 번역이 VPN 끊김으로 실패하면, 예전엔 "中 중국어 일괄"을 눌러야만 다시 채워졌다.
  // (발주처 "타오바오"가 중국어 화면에서도 한국어로만 보이던 이유)
  // 중국어 화면이거나 중국 매장이면, 단가표를 열거나 목록이 바뀔 때 빈 칸만 골라 뒤에서 다시 번역한다.
  // 같은 품목은 최대 3번, 덜 채워지면 1분 뒤 다시. 편집·추가 중에는 건드리지 않는다.
  const autoZhTries = useRef(new Map());
  const [autoZhTick, setAutoZhTick] = useState(0);
  useEffect(() => {
    if (lang !== 'zh' && !isCN) return undefined;
    if (zhRunning || editingIdx !== null || isAdding) return undefined;
    const cur = latestPricesRef.current || [];
    const need = cur.filter(p => Object.keys(priceRowZhNeeds(p)).length > 0 && (autoZhTries.current.get(koSig(p)) || 0) < 3);
    if (!need.length) return undefined;
    const timer = setTimeout(() => {
      need.forEach(p => autoZhTries.current.set(koSig(p), (autoZhTries.current.get(koSig(p)) || 0) + 1));
      const retryLater = () => setTimeout(() => setAutoZhTick(n => n + 1), 60000);
      fillPriceZh(need).then(({ next, filled }) => {
        if (filled) applyZhPatches(need.map((r, i) => [r, next[i]]));
        if (filled < need.length) retryLater();
      }).catch(retryLater);
    }, 2000);
    return () => clearTimeout(timer);
  }, [prices, lang, isCN, zhRunning, editingIdx, isAdding, autoZhTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // V161: 재료명 정리 (중국어만 적었으면 한국어 번역을 기다렸다가 저장)
  const [koBusy, setKoBusy] = useState(false);
  const koBusyRef = useRef(false);
  const saveEdit = async () => {
    if (editingIdx === null || !editRow || koBusyRef.current) return;
    const key0 = editKeyRef.current;
    const t0 = tidyNameFields(editRow);
    let er = t0.row;
    if (t0.needKo) {
      koBusyRef.current = true; setKoBusy(true);
      const ko = await koNameFor(er.name_zh);
      koBusyRef.current = false; setKoBusy(false);
      if (editKeyRef.current !== key0) return;   // 기다리는 동안 다른 줄을 열었으면 저장 안 함
      if (ko) er = { ...er, name: ko };
    }
    saveEditRow(er, { keepZh: t0.zhFromName });
  };
  const saveEditRow = (editRow, { keepZh = false } = {}) => {
    if (editingIdx === null || !editRow) return;
    // V121: 편집을 시작한 그 품목에 저장한다 (자리가 바뀌었으면 찾아서, 사라졌으면 저장하지 않음)
    let targetIdx = editingIdx;
    if (editKeyRef.current && rowKey(normalizedPrices[targetIdx]) !== editKeyRef.current) {
      targetIdx = normalizedPrices.findIndex(r => rowKey(r) === editKeyRef.current);
      if (targetIdx < 0) {
        alert(t('다른 화면에서 이 품목이 바뀌었어요. 다시 열어 수정해주세요.'));
        cancelEdit();
        return;
      }
    }
    const prevRow = normalizedPrices[targetIdx] || {};
    const lp = lossProblem(editRow);   // V152
    if (lp) { alert(lp); return; }
    const cm = clashMsg({ ...editRow, name: String(editRow.name || '').trim() }, prevRow);   // V153
    if (cm) { alert(cm); return; }
    const oldName = (prevRow.name || '').trim();
    const newName = (editRow.name || '').trim();
    const updated = [...normalizedPrices];
    const nextRow = withLoss({ ...editRow, name: newName, price: Number(editRow.price) || 0, net_g: Number(editRow.net_g) || 0,
                      code: String(editRow.code || '').trim() || makeCodeAllocator(prices)() }, editRow);   // V113: 비우면 새 번호 · V152 로스
    nextRow.link = normalizeLinkInput(editRow.link);   // V118: 붙여넣은 공유 문장에서 링크만
    if (!nextRow.link) delete nextRow.link;
    // V164: 사장님 "한국어나 중국어든 직접 채워 넣으면 둘 중 무엇을 고쳐도 다른 언어 쪽은 그대로, 빈 칸이면 자동으로"
    //   (예전 V112는 한국어를 고치면 중국어를 비우고 다시 번역 → 발주처 启臻을 '치전'으로 고치자 중국어가 '煎饼'으로 바뀌었다)
    //   · 이름·제조사·발주처: 한국어를 고쳐도 중국어 칸은 그대로. 비어 있을 때만 저장 뒤 자동 번역
    //   · 한국어 칸에 중국어로 적혀 있던 것(启臻)을 한글(치전)로 바꾸면, 원래 중국어는 비어 있는 중국어 칸으로 옮긴다
    //   · 규격·단위는 숫자·묶음이 바뀌는 것이라 예전처럼 중국어를 다시 맞춘다
    for (const [f, zf] of PRICE_ZH_FIELDS) {
      const sv = v => String(v == null ? '' : v).trim();
      const pv = sv(prevRow[f]); const nv = sv(nextRow[f]);
      if (nv === pv) continue;
      if (f === 'spec' || f === 'unit') {
        if (sv(nextRow[zf]) === sv(prevRow[zf])) nextRow[zf] = '';
        continue;
      }
      if (keepZh && f === 'name') continue;   // V161 중국어를 재료명 칸에서 옮겨 온 것
      if (!sv(nextRow[zf]) && isZhOnly(pv) && !isZhOnly(nv)) nextRow[zf] = pv;
    }
    updated[targetIdx] = nextRow;
    commitPrices(updated);

    // ── 이름 변경 시 모든 레시피의 식재료 이름도 일괄 갱신 (연동 유지) ──
    if (oldName && newName && oldName !== newName && typeof onCategoriesChange === 'function') {
      let touched = 0;
      const nextCategories = (categories || []).map(c => ({
        ...c,
        items: (c.items || []).map(item => {
          // V141: 단가표의 반제품 줄 이름을 바꾸면 그 반제품 레시피 이름도 같이 (안 그러면 다음 저장 때 옛 이름 줄이 또 생김)
          const renameSelf = item && item.type === 'semi' && (prevRow.category === '반제품' || nextRow.category === '반제품')
            && sameIngredientName(item.name, oldName);
          if (renameSelf) touched++;
          if (!Array.isArray(item.ingredients) && !renameSelf) return item;
          let changed = false;
          const newIngs = (item.ingredients || []).map(ing => {
            // 공백 무시 비교 — "해찬들 고추장" ↔ "해찬들고추장" 같이 인식
            if (sameIngredientName(ing?.name, oldName)) {
              changed = true;
              touched++;
              return { ...ing, name: newName };
            }
            return ing;
          });
          if (!changed && !renameSelf) return item;
          const out = changed ? { ...item, ingredients: newIngs } : { ...item };
          if (renameSelf) {
            out.name = newName;
            const z = String(nextRow.name_zh || '').trim();
            if (z && !/[가-힣]/.test(z)) out.name_zh = z; else delete out.name_zh;   // 옛 이름의 중국어는 틀린 번역
          }
          return out;
        }),
      }));
      if (touched > 0) {
        onCategoriesChange(nextCategories);
        try { console.info(`[단가표] "${oldName}" → "${newName}" : 레시피 ${touched}곳 자동 갱신`); } catch {}
      }
    }

    afterSaveZh(nextRow, prevRow);

    setEditingIdx(null);
    setEditRow(null);
    editKeyRef.current = null;
  };

  const deleteRow = (idx) => {
    if (!window.confirm(t('이 단가 항목을 삭제하시겠습니까?'))) return;
    commitPrices(normalizedPrices.filter((_, i) => i !== idx));
    setEditingIdx(null);
  };

  // ── V99 중국어 병기 — 일괄 생성 ──────────────────────────────────────
  // 용어집(288개) → Firestore 공용 캐시 → Haiku 순으로 찾으므로
  // 실제 API를 타는 건 "셈핏 전체에서 처음 보는 단어"뿐이다.
  const runFillZh = async () => {
    if (zhRunning) return;
    const missing = (normalizedPrices || []).filter(p => Object.keys(priceRowZhNeeds(p)).length > 0).length;
    if (missing === 0) { alert(t('모든 품목에 이미 중국어명이 있습니다.')); return; }
    if (!window.confirm(lang === 'zh'
      ? `将为 ${missing} 个缺少中文的品项（食材名称·生产厂家·供应商·规格）生成中文。\n\n` +
        `先用词库和共享缓存，只有新词才用AI翻译。\n手动修改过的中文不会被覆盖。\n\n继续吗？`
      : `중국어가 비어 있는 ${missing}개 품목(재료명·제조사·발주처·규격)의 중국어를 생성합니다.\n\n` +
        `용어집과 공용 캐시를 먼저 쓰고, 처음 보는 단어만 AI로 번역합니다.\n` +
        `직접 고친 중국어는 덮어쓰지 않습니다.\n\n계속할까요?`
    )) return;

    setZhRunning(true);
    setZhProgress({ done: 0, total: missing });
    try {
      const base = normalizedPrices;
      const { next, filled } = await fillPriceZh(base, {
        onProgress: (done, total) => setZhProgress({ done, total }),
      });
      // V121: 번역하는 동안 다른 화면에서 바뀐 내용이 있을 수 있어, 통째로 덮지 않고 '지금 목록'의 빈 칸에만 채운다
      if (filled > 0) applyZhPatches(base.map((r, i) => [r, next[i]]));
      alert(lang === 'zh' ? `已补全 ${filled} 个品项的中文。` : `${filled}개 품목의 중국어를 채웠습니다.`);
    } catch (e) {
      alert(lang === 'zh'
        ? '翻译失败：' + (e?.message || e) + '\n\n请检查网络(VPN)。韩文名称保持不变。'
        : '번역에 실패했습니다: ' + (e?.message || e) +
          '\n\n네트워크 연결(VPN)을 확인해주세요. 한국어 품목명은 그대로 유지됩니다.');
    } finally {
      setZhRunning(false);
    }
  };

  const startAdd = () => {
    setIsAdding(true);
    setNewRow({ code: '', name: '', name_zh: '', spec: '', unit: 'g', price: '', net_g: '', loss_g: '', category: tab === '전체' ? '신선식품' : tab, vendor: '', vendor_zh: '', maker: '', maker_zh: '', link: '' });
    setEditingIdx(null);
  };

  const saveNew = async () => {
    if (koBusyRef.current) return;
    const t0 = tidyNameFields(newRow);
    let nr = t0.row;
    if (!String(nr.name || '').trim()) { alert(t('재료명을 입력해주세요')); return; }
    if (String(nr.price ?? '').trim() === '') { alert(t('단가를 입력해주세요')); return; }
    if (t0.needKo) {
      koBusyRef.current = true; setKoBusy(true);
      const ko = await koNameFor(nr.name_zh);
      koBusyRef.current = false; setKoBusy(false);
      if (ko) nr = { ...nr, name: ko };
    }
    saveNewRow(nr);
  };
  const saveNewRow = (newRow) => {
    if (!newRow.name.trim()) { alert(t('재료명을 입력해주세요')); return; }
    // V125: 물처럼 무료인 재료는 0원으로 넣을 수 있다 (비워 둔 것만 막는다)
    if (String(newRow.price ?? '').trim() === '') { alert(t('단가를 입력해주세요')); return; }
    const lp = lossProblem(newRow);   // V152
    if (lp) { alert(lp); return; }
    const cm = clashMsg(newRow, null);   // V153
    if (cm) { alert(cm); return; }
    const name = newRow.name.trim();
    const code = String(newRow.code || '').trim() || makeCodeAllocator(prices)();   // V113
    const row = withLoss({ ...newRow, name, code, price: Number(newRow.price) || 0, net_g: Number(newRow.net_g) || 0 }, newRow);
    row.link = normalizeLinkInput(newRow.link);   // V118
    if (!row.link) delete row.link;
    commitPrices([...normalizedPrices, row]);
    setIsAdding(false);
    afterSaveZh(row, {});
  };

  const inputStyle = {
    padding: '5px 8px', border: '1.5px solid #EA580C',
    borderRadius: '5px', fontSize: '13px', width: '100%',
    outline: 'none', boxSizing: 'border-box',
  };

  const cellStyle = (align = 'left') => ({
    padding: '10px 14px', fontSize: '13px',
    textAlign: align, borderBottom: '1px solid #f3f4f6',
  });

  // V80: 카테고리별 뱃지 색상
  const CAT_BADGE_STYLE = {
    '신선식품': { bg: '#F4F7FB', color: '#1A56A0' },
    '공산품':   { bg: '#FAFAFA', color: '#52525B' },
    '소모품':   { bg: '#FFFBEB', color: '#92400E' },
    '반제품':   { bg: '#FEF3C7', color: '#7C2D12' },
  };
  const catBadge = (cat) => {
    const s = CAT_BADGE_STYLE[cat] || { bg: '#F3F4F6', color: '#71717A' };
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
        fontSize: '11px', fontWeight: '700',
        background: s.bg, color: s.color,
      }}>{t(cat)}</span>
    );
  };

  // V159 계정과목 — 식재료(신선·공산 기본)·소모품(소모품 기본)이 아니면 구분 옆에 표시
  const acctText = (a) => { const l = ACCT_LABEL[a] || ACCT_LABEL.etc; return `${l.icon} ${lang === 'zh' ? l.zh : l.ko}`; };
  const acctBadge = (row) => {
    if (!row || row.category === '반제품') return null;
    const a = acctOfRow(row);
    const dflt = row.category === '소모품' ? 'supply' : 'food';
    if (a === dflt) return null;
    const l = ACCT_LABEL[a];
    return <span data-acct-badge={a} style={{ display: 'inline-block', marginLeft: 4, marginTop: 2, padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, background: l.bg, color: l.color }}>{acctText(a)}</span>;
  };
  // 구분을 바꾸면 계정과목도 맞춰 준다 (소모품 ↔ 식재료)
  const withCategory = (row, category) => {
    const next = { ...row, category };
    const a = acctOfRow(row);
    if (category === '소모품' && (a === 'food' || a === 'drink')) next.acct = 'supply';
    else if ((category === '신선식품' || category === '공산품') && a !== 'food' && a !== 'drink') delete next.acct;
    return next;
  };
  // 계정과목을 고르면 구분도 맞춰 준다 (식재료가 아니면 소모품)
  const withAcct = (row, acct) => {
    const next = { ...row, acct };
    if (acct !== 'food' && acct !== 'drink' && row.category !== '소모품') next.category = '소모품';
    if ((acct === 'food' || acct === 'drink') && row.category === '소모품') next.category = '공산품';
    if (acct === 'food') delete next.acct;   // 식재료는 기본값 — 따로 저장하지 않음
    return next;
  };

  const TABS = ['전체', '신선식품', '공산품', '소모품', '반제품'];

  // V112: 업체를 제조사·발주처로 나눔. 중국 매장은 한국 시장가 2열을 뺀다.
  // (기존 '업체'(vendor) 값은 발주처 — 발주서·공급처 묶음이 이 값을 쓴다)
  const COLS = [
    { key: 'code',     label: t('코드') },
    { key: 'name',     label: t('재료명') },
    { key: 'category', label: t('구분') },
    { key: 'maker',    label: t('제조사') },
    { key: 'vendor',   label: t('발주처') },
    { key: 'link',     label: t('구매 링크'), align: 'center' },   // V118
    { key: 'spec',     label: t('규격') },
    { key: 'unit',     label: t('단위') },
    { key: 'price',    label: `${t('단가')} (${unit})`, align: 'right' },
    { key: 'net_g',    label: Z('중량 (g)', '重量 (g)'), align: 'right',   // V152
      title: Z('신선식품: 구매 중량 − 로스 = 실사용량\n공산품: NET 중량 (포장 뺀 내용물)\n소모품·반제품: 기준량',
               '生鲜：购买重量 − 损耗 = 实际可用量\n干货：净含量（不含包装）\n耗材·半成品：基准量') },
    { key: 'avg',      label: t('내 평균가 (30일)'), align: 'center' },
    ...(isCN ? [] : [
      { key: 'market', label: t('시장가 (도매·kg)'), align: 'center' },
      { key: 'naver',  label: t('네이버 최저가'), align: 'center' },
    ]),
    { key: 'manage',   label: t('관리'), align: 'center' },
  ];
  const zhInput = { fontSize: '11px', borderColor: '#D4D4D8', color: '#52525B', marginTop: 4 };

  return (
    <div style={{ flex: 1, padding: '24px', overflowY: 'auto', background: '#f8fafb' }}>

      {/* 단가표 안내 힌트 */}
      <Hint hintKey="price_intro" mb={16}
        zh={<><strong>配方成本计算的核心！</strong>请在这里录入原材料单价。输入单价和基准量(g)后，配方会按食材用量<strong>自动计算成本</strong>。单价一改，所有配方成本立即更新。</>}>
        <strong>레시피 원가 계산의 핵심!</strong> 여기에 원재료 단가를 입력해두세요.
        단가·기준량(g)을 입력하면 레시피에서 재료 사용량에 따라 <strong>원가가 자동으로 계산</strong>됩니다.
        단가가 바뀌면 모든 레시피 원가에 즉시 반영돼요.
      </Hint>

      {/* 상단 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#EA580C' }}>{t('단가표 관리')}</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{lang === 'zh' ? `共 ${prices.length} 项` : `총 ${prices.length}개 항목`}</p>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('재료명 또는 코드 검색...')}
          style={{
            flex: 1, minWidth: '180px', padding: '9px 14px',
            border: '1.5px solid #e5e7eb', borderRadius: '8px',
            fontSize: '13px', outline: 'none',
          }}
        />
        <button onClick={startAdd}
          data-tour="prices-add-item"
          style={{
            padding: '9px 18px', borderRadius: '8px', border: 'none',
            background: '#EA580C', color: '#fff', fontSize: '13px',
            fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
          {t('+ 항목 추가')}
        </button>
        {/* 🇨🇳 V99 중국어 일괄 생성 — 허페이 매장 직원용 병기 */}
        <button onClick={runFillZh} disabled={zhRunning}
          title={t('중국어명이 비어 있는 품목을 한 번에 채웁니다 (직접 고친 건 유지)')}
          style={{
            padding: '9px 14px', borderRadius: '8px',
            border: '1.5px solid #1A56A0',
            background: zhRunning ? '#F4F4F5' : '#fff',
            color: zhRunning ? '#9ca3af' : '#1A56A0',
            fontSize: '12px', fontWeight: '700',
            cursor: zhRunning ? 'default' : 'pointer', whiteSpace: 'nowrap',
          }}>
          {zhRunning
            ? `${t('번역 중...')} ${zhProgress.done}/${zhProgress.total}`
            : t('中 중국어 일괄')}
        </button>
        {/* 📥 단가표 엑셀 다운로드 */}
        <button onClick={async () => {
          try {
            // V127: 발음 열 — 한국어 화면은 이름 중국어의 병음, 중국어 화면은 한국어 이름의 로마자
            if (lang !== 'zh' && !isPinyinReady()) await ensurePinyin();
            const aoa = buildPricesSheet(prices || [], {
              pron: {
                header: lang === 'zh' ? '한국어 발음(韩语读音)' : '병음(拼音)',
                of: (p) => pronText(p.name, p.name_zh, lang),
              },
            });
            const d = new Date();
            const fileName = `단가표_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}.xlsx`;
            exportSingleSheet({ fileName, sheetName: '단가표', aoa });
          } catch (e) { alert('다운로드 실패: ' + e.message); }
        }}
          title={t('단가표 전체를 엑셀로 저장')}
          style={{
            padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #FF6F0F',
            background: '#fff', color: '#18181B', fontSize: '12px',
            fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
          {t('📥 엑셀')}
        </button>
        {/* V154: 종이로 쓰는 직원용 — 단가표(와 레시피)를 골라서 인쇄 */}
        <button onClick={() => setShowPrint(true)} data-price-print
          style={{
            padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #18181B',
            background: '#fff', color: '#18181B', fontSize: '12px',
            fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
          🖨 {lang === 'zh' ? '打印' : '인쇄'}
        </button>
        {/* V143: 다른 업체에 '이 식재료 있나요?' — 위챗으로 보낼 목록 (가격 없음, 타오바오 링크 포함) */}
        <button onClick={() => setShowInquiry(true)} data-inquiry-open
          title={t('고른 발주처의 품목을 위챗으로 다른 업체에 물어볼 목록으로 만들어요 (가격 없음)')}
          style={{
            padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #07C160',
            background: '#fff', color: '#067D3F', fontSize: '12px',
            fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
          {t('💬 업체 문의 목록')}
        </button>
        {/* ── 원클릭 통합 가격 조회 ── (V112: 중국 매장은 숨김) */}
        {!isCN && (
        <button onClick={runAutoFetch} disabled={autoRunning}
          data-tour="prices-auto-fetch"
          title={t('시장가 + 네이버 + AI 매칭을 한 번에 자동 실행 (미매칭만 AI 호출)')}
          style={{
            padding: '10px 18px', borderRadius: '10px',
            border: 'none',
            background: autoRunning
              ? '#9ca3af'
              : '#EA580C',
            color: '#fff', fontSize: '13px',
            fontWeight: '800', cursor: autoRunning ? 'wait' : 'pointer',
            whiteSpace: 'nowrap',
            boxShadow: autoRunning ? 'none' : '0 2px 8px rgba(30,107,60,0.3)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
          {autoRunning
            ? <>⏳ {autoMessage || '조회 중...'}</>
            : <>{t('💎 시장 가격 자동 조회')}</>
          }
        </button>
        )}
      </div>

      {/* ── 진행 상황 / 결과 요약 배너 ── */}
      {autoRunning && (
        <div style={{
          background: '#FAFAFA', border: '1.5px solid #D4D4D8',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          fontSize: 12, color: '#52525B', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>⚙️</span>
          <span>{autoMessage}</span>
          {autoStep === 'ai' && aiProgress.total > 0 && (
            <span style={{ marginLeft: 'auto', color: '#7c3aed' }}>
              {aiProgress.done}/{aiProgress.total}
            </span>
          )}
        </div>
      )}
      {!autoRunning && autoSummary && (
        <div style={{
          background: '#FAFAFA', border: '1.5px solid #FED7AA',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          fontSize: 12, color: '#EA580C', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 16 }}>✅</span>
          <span>조회 완료</span>
          <span style={{ color: '#374151', fontWeight: 600 }}>
            전체 <strong>{autoSummary.total}</strong>개 중 시장가 <strong>{autoSummary.kamis}</strong>
            {' · '}네이버 <strong>{autoSummary.naver}</strong>
            {autoSummary.ai > 0 && <> · AI 추가 <strong style={{ color: '#7c3aed' }}>{autoSummary.ai}</strong></>}
            {autoSummary.unmatched > 0 && (
              <> · <span style={{ color: '#9ca3af' }}>미매칭 {autoSummary.unmatched}</span></>
            )}
          </span>
          <button onClick={() => setAutoSummary(null)}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: '#9ca3af', cursor: 'pointer', fontSize: 12,
            }}>닫기 ✕</button>
        </div>
      )}

      {/* ── 개별 조회 (파워 유저용) ── (V112: 중국 매장은 숨김) */}
      {!isCN && (
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowAdvanced(s => !s)}
          style={{
            background: 'none', border: 'none', color: '#9ca3af',
            fontSize: 11, cursor: 'pointer', padding: '4px 0',
          }}>
          {t('개별 조회')} {showAdvanced ? '▴' : '▾'}
        </button>
        {showAdvanced && (
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button onClick={loadNaverPrices} disabled={naverLoading}
              title={t('네이버 쇼핑 최저가 일괄 조회 (캐시 6시간)')}
              style={{
                padding: '7px 12px', borderRadius: 7,
                border: '1.5px solid #FF6F0F',
                background: naverFetched ? '#FAFAFA' : '#fff',
                color: '#18181B', fontSize: 12,
                fontWeight: 700, cursor: naverLoading ? 'wait' : 'pointer',
                opacity: naverLoading ? 0.6 : 1,
              }}>
              {t(naverLoading ? '⏳ 조회 중...' : naverFetched ? '🔄 네이버 다시' : '🛒 네이버만')}
            </button>
            <button onClick={() => enhanceWithAi(false)} disabled={aiLoading}
              title={t('시장가·네이버 매칭 안 된 항목을 Claude Haiku로 표준명 추정')}
              style={{
                padding: '7px 12px', borderRadius: 7,
                border: '1.5px solid #7c3aed',
                background: aiCanonicalMap.size > 0 ? '#faf5ff' : '#fff',
                color: '#7c3aed', fontSize: 12,
                fontWeight: 700, cursor: aiLoading ? 'wait' : 'pointer',
                opacity: aiLoading ? 0.6 : 1,
              }}>
              {aiLoading ? `⏳ AI ${aiProgress.done}/${aiProgress.total}` : t('🤖 AI만')}
            </button>
          </div>
        )}
      </div>
      )}

      {/* 탭 */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        {TABS.map(cat => (
          <button key={cat} onClick={() => setTab(cat)}
            style={{
              padding: '8px 18px', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '700',
              border: tab === cat ? 'none' : '1.5px solid #e5e7eb',
              background: tab === cat ? '#EA580C' : '#fff',
              color: tab === cat ? '#fff' : '#6b7280',
            }}>
            {t(cat)} <span style={{
              marginLeft: '4px', fontSize: '11px',
              opacity: 0.8,
            }}>({counts[cat]})</span>
          </button>
        ))}
      </div>

      {/* 구분 설명 힌트 */}
      <Hint hintKey="price_categories_v80" mb={12}
        zh={<><strong>生鲜</strong>是蔬菜·水果·肉类·水产·乳制品，<strong>加工品</strong>是加工食品·酱料·面·罐头，<strong>耗材</strong>是塑料袋·洗涤剂·一次性餐盒等非食材的店铺物品。<strong>半成品</strong>是按配方自制的酱汁·高汤·底料，单价自动计算。<strong>重量</strong>：生鲜填购买重量和损耗，干货填净含量。</>}>
        <strong>신선식품</strong>은 야채·과일·육류·수산·유제품, <strong>공산품</strong>은 가공식품·소스·면·통조림,
        <strong> 소모품</strong>은 비닐·세제·일회용기 등 식재료가 아닌 매장 자재예요.
        <strong> 반제품</strong>은 레시피로 직접 만드는 소스·육수·밑재료고 단가는 자동 계산됩니다.
        <strong> 중량</strong>: 신선식품은 구매 중량과 로스, 공산품은 NET 중량을 넣어요.
      </Hint>
      {/* V152 */}
      <Hint hintKey="price_yield_v152" mb={12}
        zh={<>⚖️ <strong>生鲜</strong>填购买重量和<strong>损耗</strong>（处理时去掉的皮、根等），成本按实际可用量计算（洋葱 5000g − 损耗 500g = 4500g）。<strong>干货</strong>填<strong>净含量</strong>（不含包装的内容物，例：酱油 1.8L = 1800g）。订货量也会把损耗算进去。</>}>
        ⚖️ <strong>신선식품</strong>은 구매 중량과 <strong>로스</strong>(손질하며 버리는 껍질·뿌리 등)를 넣으면 실사용량으로 원가를 계산해요 (양파 5000g − 로스 500g = 4500g).
        <strong> 공산품</strong>은 포장을 뺀 <strong>NET 중량</strong>(내용물)을 넣어요 (간장 1.8L = 1800g). 발주량 계산도 로스만큼 늘어나요.
      </Hint>
      <Hint hintKey="price_row_edit" mb={12}
        zh={<>点击表格中的<strong>行</strong>就能直接修改。单价或规格变了，点一下即可快速更新。</>}>
        💡 표의 <strong>행을 클릭</strong>하면 그 자리에서 바로 수정할 수 있어요. 단가나 규격이 바뀌면 클릭해서 빠르게 갱신하세요.
      </Hint>

      {/* 테이블 (V34: 모바일 가로 스크롤) */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
                    overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', minWidth: isCN ? '1070px' : '1230px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1A56A0', color: '#fff' }}>
              {COLS.map(c => (
                <th key={c.key} title={c.title || undefined} style={{
                  padding: '12px 14px', textAlign: c.align || 'left',
                  fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap',
                }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>

            {/* 새 항목 추가 행 */}
            {isAdding && (
              <tr style={{ background: '#FAFAFA', borderBottom: '2px solid #EA580C' }}>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.code} onChange={e => setNewRow({ ...newRow, code: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={t('코드 (비우면 자동)')} style={inputStyle} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.name} onChange={e => setNewRow({ ...newRow, name: e.target.value })}
                    data-new-name onKeyDown={handleNewKeyDown} placeholder={lang === 'zh' ? '食材名称 *（写中文就行）' : `${t('재료명')} * (중국어만 써도 돼요)`} style={inputStyle} />
                  <input value={newRow.name_zh || ''} onChange={e => setNewRow({ ...newRow, name_zh: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={`中文 (${t('비워두면 자동')})`}
                    style={{ ...inputStyle, marginTop: 4, fontSize: '11px',
                             borderColor: '#D4D4D8', color: '#52525B' }} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <select value={newRow.category} onChange={e => setNewRow(withCategory(newRow, e.target.value))}
                    onKeyDown={handleNewKeyDown} style={{ ...inputStyle, background: '#fff' }}>
                    {PRICE_CATS.map(c => <option key={c} value={c}>{t(c)}</option>)}
                  </select>
                  {newRow.category !== '반제품' && (
                    <select data-acct-new value={acctOfRow(newRow)} onChange={e => setNewRow(withAcct(newRow, e.target.value))}
                      title={t('계정과목')} style={{ ...inputStyle, background: '#fff', marginTop: 4, fontSize: '11px', borderColor: '#D4D4D8' }}>
                      {ACCTS.map(a => <option key={a} value={a}>{acctText(a)}</option>)}
                    </select>
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.maker || ''} onChange={e => setNewRow({ ...newRow, maker: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={t('제조사')} style={inputStyle} />
                  <input value={newRow.maker_zh || ''} onChange={e => setNewRow({ ...newRow, maker_zh: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={`中文 (${t('비워두면 자동')})`} style={{ ...inputStyle, ...zhInput }} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <VendorPicker value={newRow.vendor || ''} options={vendorOptions}
                    onChange={v => setNewRow(r => ({ ...r, vendor: v }))}
                    onPick={o => setNewRow(r => ({ ...r, vendor: o.name, vendor_zh: o.zh || '' }))}
                    onKeyDown={handleNewKeyDown} placeholder={t('발주처')} style={inputStyle}
                    countLabel={n => (lang === 'zh' ? `${n}项` : `${n}품목`)} />
                  <input value={newRow.vendor_zh || ''} onChange={e => setNewRow({ ...newRow, vendor_zh: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={`中文 (${t('비워두면 자동')})`} style={{ ...inputStyle, ...zhInput }} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.link || ''} onChange={e => setNewRow({ ...newRow, link: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={t('링크 붙여넣기')} style={{ ...inputStyle, fontSize: '12px' }} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.spec} onChange={e => setNewRow({ ...newRow, spec: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={t('규격')} style={inputStyle} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input value={newRow.unit} onChange={e => setNewRow({ ...newRow, unit: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={t('단위')} style={inputStyle} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input type="number" value={newRow.price} onChange={e => setNewRow({ ...newRow, price: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={`${t('단가')} *`} style={{ ...inputStyle, textAlign: 'right' }} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <input data-weight-input type="number" value={newRow.net_g} onChange={e => setNewRow({ ...newRow, net_g: e.target.value })}
                    onKeyDown={handleNewKeyDown} placeholder={weightPlaceholder(newRow.category)} style={{ ...inputStyle, textAlign: 'right' }} />
                  {usesLoss(newRow) && (
                    <input data-loss-input type="number" value={newRow.loss_g ?? ''} onChange={e => setNewRow({ ...newRow, loss_g: e.target.value })}
                      onKeyDown={handleNewKeyDown} placeholder={Z('로스 (g)', '损耗 (g)')}
                      style={{ ...inputStyle, textAlign: 'right', marginTop: 4, borderColor: '#FDBA74' }} />
                  )}
                  {lossPreview(newRow)}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td>
                {!isCN && <><td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td><td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td></>}
                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    <button data-save-new onClick={saveNew} disabled={koBusy}
                      style={{ padding: '5px 12px', borderRadius: '6px', border: 'none',
                               background: '#EA580C', color: '#fff', fontSize: '12px',
                               fontWeight: '700', cursor: 'pointer', opacity: koBusy ? 0.7 : 1 }}>{koBusy ? (lang === 'zh' ? '正在翻译韩语…' : '한국어 번역 중…') : t('저장')}</button>
                    <button onClick={() => setIsAdding(false)}
                      style={{ padding: '5px 10px', borderRadius: '6px',
                               border: '1px solid #e5e7eb', background: '#fff',
                               fontSize: '12px', cursor: 'pointer' }}>{t('취소')}</button>
                  </div>
                </td>
              </tr>
            )}

            {filtered.map((row, fi) => {
              const isEditing = editingIdx === row._idx;
              const cs = { ...cellStyle(), cursor: 'text' };
              const oc = (col) => ({ onClick: () => startEdit(row._idx, col) });
              // V81: vendor가 바뀔 때마다 그룹 헤더 행 삽입
              const curVendor = (row.vendor || '').trim() || '업체 미지정';
              const prevVendor = fi === 0 ? null : ((filtered[fi - 1].vendor || '').trim() || '업체 미지정');
              const isNewVendorGroup = curVendor !== prevVendor;
              return (
                <React.Fragment key={row._idx}>
                {isNewVendorGroup && (
                  <tr style={{ background: '#FAFAFA' }}>
                    <td colSpan={COLS.length} style={{
                      padding: '10px 14px',
                      fontSize: '12px', fontWeight: '800',
                      color: curVendor === '업체 미지정' ? '#A1A1AA' : '#18181B',
                      borderTop: '2px solid #E4E4E7',
                      borderBottom: '1px solid #E4E4E7',
                      letterSpacing: '-0.01em',
                    }}>
                      {curVendor === '업체 미지정' ? t('발주처 미지정') : curVendor}
                      {curVendor !== '업체 미지정' && validCompanyZh(row.vendor_zh) && (
                        <span style={{ marginLeft: 8, color: '#1A56A0', fontWeight: 600 }}>{row.vendor_zh}</span>
                      )}
                    </td>
                  </tr>
                )}
                <tr
                  title={isEditing ? '' : t('클릭하여 수정 (Enter=저장, Esc=취소)')}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: isEditing ? '#FAFAFA' : fi % 2 === 0 ? '#fff' : '#fafafa',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = '#FAFAFA'; }}
                  onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = fi % 2 === 0 ? '#fff' : '#fafafa'; }}
                >
                  {isEditing ? (
                    <>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.code || ''} onChange={e => setEditRow({ ...editRow, code: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'code'} style={inputStyle} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.name || ''} onChange={e => setEditRow({ ...editRow, name: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'name'} style={inputStyle} />
                        <input data-edit-row value={editRow.name_zh || ''} onChange={e => setEditRow({ ...editRow, name_zh: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          placeholder={`中文 (${t('비워두면 자동')})`}
                          style={{ ...inputStyle, marginTop: 4, fontSize: '11px',
                                   borderColor: '#D4D4D8', color: '#52525B' }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <select data-edit-row value={editRow.category || '원재료'} onChange={e => setEditRow(withCategory(editRow, e.target.value))}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'category'} style={{ ...inputStyle, background: '#fff' }}>
                          {PRICE_CATS.map(c => <option key={c} value={c}>{t(c)}</option>)}
                        </select>
                        {editRow.category !== '반제품' && (
                          <select data-edit-row data-acct-edit value={acctOfRow(editRow)} onChange={e => setEditRow(withAcct(editRow, e.target.value))}
                            onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                            title={t('계정과목')} style={{ ...inputStyle, background: '#fff', marginTop: 4, fontSize: '11px', borderColor: '#D4D4D8' }}>
                            {ACCTS.map(a => <option key={a} value={a}>{acctText(a)}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.maker || ''} onChange={e => setEditRow({ ...editRow, maker: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'maker'} placeholder={t('제조사')} style={inputStyle} />
                        <input data-edit-row value={editRow.maker_zh || ''} onChange={e => setEditRow({ ...editRow, maker_zh: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          placeholder={`中文 (${t('비워두면 자동')})`} style={{ ...inputStyle, ...zhInput }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <VendorPicker editRow value={editRow.vendor || ''} options={vendorOptions}
                          onChange={v => setEditRow(r => ({ ...r, vendor: v }))}
                          onPick={o => setEditRow(r => ({ ...r, vendor: o.name, vendor_zh: o.zh || '' }))}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'vendor'} placeholder={t('발주처')} style={inputStyle}
                          countLabel={n => (lang === 'zh' ? `${n}项` : `${n}품목`)} />
                        <input data-edit-row value={editRow.vendor_zh || ''} onChange={e => setEditRow({ ...editRow, vendor_zh: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          placeholder={`中文 (${t('비워두면 자동')})`} style={{ ...inputStyle, ...zhInput }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.link || ''} onChange={e => setEditRow({ ...editRow, link: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'link'} placeholder={t('링크 붙여넣기')} style={{ ...inputStyle, fontSize: '12px' }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.spec || ''} onChange={e => setEditRow({ ...editRow, spec: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'spec'} style={inputStyle} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row value={editRow.unit || ''} onChange={e => setEditRow({ ...editRow, unit: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'unit'} style={inputStyle} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row type="number" value={editRow.price || ''} onChange={e => setEditRow({ ...editRow, price: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur}
                          autoFocus={focusCol === 'price'} style={{ ...inputStyle, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <input data-edit-row data-weight-input type="number" value={editRow.net_g || ''} onChange={e => setEditRow({ ...editRow, net_g: e.target.value })}
                          onKeyDown={handleEditKeyDown} onBlur={handleEditBlur} placeholder={weightPlaceholder(editRow.category)}
                          autoFocus={focusCol === 'net_g'} style={{ ...inputStyle, textAlign: 'right' }} />
                        {usesLoss(editRow) && (
                          <input data-edit-row data-loss-input type="number" value={editRow.loss_g ?? ''} onChange={e => setEditRow({ ...editRow, loss_g: e.target.value })}
                            onKeyDown={handleEditKeyDown} onBlur={handleEditBlur} placeholder={Z('로스 (g)', '损耗 (g)')}
                            style={{ ...inputStyle, textAlign: 'right', marginTop: 4, borderColor: '#FDBA74' }} />
                        )}
                        {lossPreview(editRow)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td>
                      {!isCN && <><td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td><td style={{ padding: '8px 10px', textAlign: 'center', color: '#d1d5db', fontSize: 11 }}>—</td></>}
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}
                          onClick={e => e.stopPropagation()}>
                          <button data-edit-row data-save-edit onClick={saveEdit} disabled={koBusy}
                            style={{ padding: '5px 10px', borderRadius: '6px', border: 'none',
                                     background: '#EA580C', color: '#fff', fontSize: '12px',
                                     fontWeight: '700', cursor: 'pointer', opacity: koBusy ? 0.7 : 1 }}>{koBusy ? (lang === 'zh' ? '翻译中…' : '번역 중…') : t('저장')}</button>
                          <button data-edit-row onClick={cancelEdit}
                            style={{ padding: '5px 8px', borderRadius: '6px',
                                     border: '1px solid #e5e7eb', background: '#fff',
                                     fontSize: '12px', cursor: 'pointer' }}>{t('취소')}</button>
                          <button data-edit-row onClick={() => deleteRow(row._idx)}
                            style={{ padding: '5px 8px', borderRadius: '6px',
                                     border: '1px solid #E4E4E7', background: '#fff5f5',
                                     color: '#52525B', fontSize: '12px', cursor: 'pointer' }}>{t('삭제')}</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...cs, color: '#9ca3af', fontSize: '12px' }} {...oc('code')}>{row.code || '-'}</td>
                      <td style={{ ...cs, fontWeight: '600' }} {...oc('name')}>
                        {/* V126: 병기 + 발음 (한국어 화면은 병음, 중국어 화면은 한국어 로마자) */}
                        <BiName ko={row.name} zh={row.name_zh} />
                      </td>
                      <td style={cs} {...oc('category')}>{catBadge(row.category)}{acctBadge(row)}</td>
                      <td style={{ ...cs, fontWeight: '600', color: row.maker ? '#374151' : '#d1d5db' }} {...oc('maker')}>
                        {row.maker ? <BiName ko={row.maker} zh={validCompanyZh(row.maker_zh) ? row.maker_zh : ''} pron={false} /> : '—'}
                      </td>
                      <td style={{ ...cs, fontWeight: '600', color: row.vendor ? '#EA580C' : '#d1d5db' }} {...oc('vendor')}>
                        {row.vendor ? <BiName ko={row.vendor} zh={validCompanyZh(row.vendor_zh) ? row.vendor_zh : ''} pron={false} /> : '—'}
                      </td>
                      <td style={{ ...cs, textAlign: 'center' }} {...oc('link')}>
                        {(() => {
                          // V118: 구매 링크 — http/https만 연다
                          const raw = String(row.link || '').trim();
                          if (!raw) return <span style={{ color: '#d1d5db' }}>—</span>;
                          const href = safeLinkHref(raw);
                          if (!href) return <span title={raw} style={{ fontSize: 11, color: '#B45309' }}>⚠ {t('링크 아님')}</span>;
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()} title={`${t('클릭해서 구매 페이지 열기')}\n${href}`}
                              style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                                       textDecoration: 'none', color: '#1A56A0', fontWeight: 700, fontSize: 12 }}>
                              <span>🔗 {t('열기')}</span>
                              <span style={{ fontSize: 10, fontWeight: 500, color: '#9ca3af', marginTop: 1 }}>{linkLabel(href)}</span>
                            </a>
                          );
                        })()}
                      </td>
                      <td style={{ ...cs, color: '#6b7280' }} {...oc('spec')}>
                        {row.spec ? <BiName ko={row.spec} zh={specToZh(row.spec) || row.spec_zh} pron={false} /> : '-'}
                      </td>
                      <td style={{ ...cs, color: '#6b7280' }} {...oc('unit')}>
                        <BiName ko={row.unit || 'g'} zh={unitToZh(row.unit) || row.unit_zh} pron={false} />
                      </td>
                      <td style={{ ...cs, textAlign: 'right', fontWeight: '700', color: '#FF8F00' }} {...oc('price')}>
                        <div>{money(row.price)}</div>
                        {pricePerUsableKg(row) > 0 && (
                          <div data-price-perkg style={{ fontSize: 10, fontWeight: lossG(row) ? 700 : 500, color: lossG(row) ? '#16A34A' : '#9ca3af', marginTop: 2 }}
                            title={lossG(row) ? Z('로스를 뺀 실사용량 기준 kg당 단가', '去掉损耗后按实际可用量算的每公斤单价') : undefined}>
                            {lossG(row) ? `${Z('실사용', '实际')} ` : ''}{money(pricePerUsableKg(row))}/kg
                          </div>
                        )}
                      </td>
                      <td style={{ ...cs, textAlign: 'right', color: '#1e40af' }} {...oc('net_g')} data-weight-cell>
                        {row.net_g ? `${row.net_g}g` : '-'}
                        {/* V152: 신선식품 로스 → 실사용량 / 공산품 NET */}
                        {usesLoss(row) && Number(row.net_g) > 0 && (lossG(row) > 0 ? (
                          <div data-weight-loss style={{ fontSize: 10, marginTop: 2, whiteSpace: 'nowrap' }}>
                            <div style={{ color: '#B45309' }}>− {Z('로스', '损耗')} {lossG(row)}g</div>
                            <div style={{ color: '#16A34A', fontWeight: 700 }}>= {Z('실사용', '实际可用')} {usableG(row)}g ({yieldPct(row)}%)</div>
                          </div>
                        ) : (
                          <div data-weight-noloss style={{ fontSize: 10, color: '#D4D4D8', marginTop: 2, whiteSpace: 'nowrap' }}>{Z('로스 미입력', '未填损耗')}</div>
                        ))}
                        {row.category === NET_CATEGORY && Number(row.net_g) > 0 && (
                          <div data-weight-net style={{ fontSize: 10, color: '#71717A', marginTop: 2 }}>NET</div>
                        )}
                      </td>
                      <td style={{ ...cs, textAlign: 'right' }}>
                        {(() => {
                          const stat = priceAverages.get(row.code);
                          if (!stat || stat.count === 0) {
                            return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                          }
                          const diffPct = row.price > 0 ? ((stat.avg - row.price) / row.price) * 100 : 0;
                          const arrow = diffPct > 5 ? '↑' : diffPct < -5 ? '↓' : '→';
                          const color = diffPct > 5 ? '#52525B' : diffPct < -5 ? '#FF6F0F' : '#6b7280';
                          return (
                            <div>
                              <div style={{ fontWeight: 700, color }}>
                                {money(stat.avg)} {arrow}
                              </div>
                              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                                {lang === 'zh' ? `${stat.count}笔` : `${stat.count}건`} · {Math.abs(diffPct).toFixed(0)}%
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      {!isCN && (<>
                      <td style={{ ...cs, textAlign: 'right' }}>
                        {(() => {
                          if (marketLoading) return <span style={{ color: '#d1d5db', fontSize: 11 }}>...</span>;
                          const km = matchWithAiFallback(row);
                          if (!km || !km.pricePerKg) return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                          const cmp = compareUserVsMarket(row, km);
                          const aiUsed = aiCanonicalMap.has(row.name);
                          const color = cmp.status === 'expensive' ? '#52525B'
                                      : cmp.status === 'cheap' ? '#FF6F0F' : '#374151';
                          const trendArrow = km.trend7d?.direction === 'up' ? '↑'
                                           : km.trend7d?.direction === 'down' ? '↓' : '→';
                          const trendColor = km.trend7d?.direction === 'up' ? '#52525B'
                                           : km.trend7d?.direction === 'down' ? '#FF6F0F' : '#9ca3af';
                          return (
                            <div>
                              <div style={{ fontWeight: 700, color }}>{money(km.pricePerKg)}/kg</div>
                              <div style={{ fontSize: 10, color: trendColor, marginTop: 2 }}>
                                {trendArrow} {Math.abs(km.trend7d?.pct || 0).toFixed(0)}% (1주)
                              </div>
                              {cmp.diffPct !== null && cmp.status !== 'fair' && (
                                <div style={{ fontSize: 9, color, marginTop: 1, fontWeight: 600 }}>
                                  내 단가 {cmp.diffPct > 0 ? '+' : ''}{cmp.diffPct}%
                                </div>
                              )}
                              {aiUsed && (
                                <div style={{ fontSize: 9, color: '#7c3aed', marginTop: 1, fontWeight: 700 }}
                                     title={`AI 표준명: ${aiCanonicalMap.get(row.name)}`}>
                                  🤖 AI
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ ...cs, textAlign: 'right' }}>
                        {(() => {
                          if (naverLoading) return <span style={{ color: '#d1d5db', fontSize: 11 }}>...</span>;
                          if (!naverFetched) return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                          const nv = naverWithAiFallback(row);
                          if (!nv || !nv.items?.length) return <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>;
                          const cmp = compareUserVsNaver(row, nv);
                          if (!cmp.naverKg) {
                            return (
                              <div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{money(nv.lprice)}~</div>
                                <div style={{ fontSize: 9, color: '#d1d5db' }}>단위 환산 불가</div>
                              </div>
                            );
                          }
                          const color = cmp.status === 'expensive' ? '#52525B'
                                      : cmp.status === 'cheap' ? '#FF6F0F' : '#374151';
                          return (
                            <div>
                              <div style={{ fontWeight: 700, color }}>{money(cmp.naverKg)}/kg</div>
                              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                                {(cmp.item?.mallName || '네이버').slice(0, 8)}
                              </div>
                              {cmp.diffPct !== null && cmp.status !== 'fair' && (
                                <div style={{ fontSize: 9, color, marginTop: 1, fontWeight: 600 }}>
                                  내 단가 {cmp.diffPct > 0 ? '+' : ''}{cmp.diffPct}%
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      </>)}
                      <td style={{ ...cellStyle('center') }}>
                        <button
                          onClick={e => { e.stopPropagation(); deleteRow(row._idx); }}
                          style={{ padding: '4px 10px', borderRadius: '6px',
                                   border: '1px solid #E4E4E7', background: '#fff5f5',
                                   color: '#52525B', fontSize: '12px', cursor: 'pointer' }}>{t('삭제')}</button>
                      </td>
                    </>
                  )}
                </tr>
                </React.Fragment>
              );
            })}

            {filtered.length === 0 && !isAdding && (
              <tr>
                <td colSpan={COLS.length} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {t(search ? '검색 결과가 없습니다' : '항목이 없습니다')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showInquiry && <InquiryListModal prices={prices || []} onClose={() => setShowInquiry(false)} />}
      {showPrint && <PrintPicker categories={categories || []} prices={normalizedPrices || []} initial={{ recipes: false, prices: true }}
        isMobile={typeof window !== 'undefined' && window.innerWidth < 768} onClose={() => setShowPrint(false)} />}
    </div>
  );
}

export default PriceManager;
